import { useMemo } from "react";
import { useI18n } from "@/lib/i18n";

const SPANS = { "90d": 90, "12m": 365, "3y": 1095 };
const DAY = 86400000;
const STATUS_BAR = { todo: "bg-slate-400", in_progress: "bg-[#2176AE]", done: "bg-emerald-600", cancelled: "bg-zinc-300" };

function ticks(start, view, lang) {
  const out = [];
  const d = new Date(start);
  d.setDate(1);
  const step = view === "90d" ? 0 : view === "12m" ? 1 : 3;
  if (view === "90d") {
    for (let i = 0; i <= 90; i += 15) out.push({ at: i, label: new Date(start.getTime() + i * DAY).toLocaleDateString(lang, { day: "2-digit", month: "short" }) });
    return out;
  }
  while (d.getTime() < start.getTime() + SPANS[view] * DAY) {
    const at = (d.getTime() - start.getTime()) / DAY;
    if (at >= 0) out.push({ at, label: d.toLocaleDateString(lang, view === "12m" ? { month: "short" } : { month: "short", year: "2-digit" }) });
    d.setMonth(d.getMonth() + step);
  }
  return out;
}

export default function Gantt({ actions, view, onOpen }) {
  const { tx, lang } = useI18n();
  const span = SPANS[view];
  const start = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - Math.round(span * 0.05)); return d; }, [span]);
  const end = start.getTime() + span * DAY;
  const pct = (iso) => Math.min(100, Math.max(0, ((new Date(iso).getTime() - start.getTime()) / DAY / span) * 100));
  const rows = actions.filter((a) => a.status !== "cancelled" && new Date(a.start_date).getTime() < end && new Date(a.due_date).getTime() >= start.getTime());
  const hidden = actions.filter((a) => a.status !== "cancelled").length - rows.length;
  const today = pct(new Date().toISOString());
  return (
    <div data-testid={`gantt-${view}`}>
      <div className="relative ml-[220px] h-6 border-b border-slate-200 text-[10px] text-slate-500">
        {ticks(start, view, lang).filter((t) => t.at / span < 0.95).map((t) => <span key={t.at} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${(t.at / span) * 100}%` }}>{t.label}</span>)}
      </div>
      {rows.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">{tx("Aucune action dans cette fenêtre", "No actions in this window")}</div>}
      {rows.map((a) => {
        const l = pct(a.start_date);
        const w = Math.max(0.8, pct(a.due_date) - l);
        return (
          <div key={a.id} className="flex items-center h-9 border-b border-slate-100 hover:bg-[#EAF3FA]/50 cursor-pointer" onClick={() => onOpen(a)} data-testid={`gantt-row-${a.code}`}>
            <div className="w-[220px] shrink-0 pr-2 text-xs truncate"><span className="font-mono text-slate-500 mr-1">{a.code}</span>{a.title}</div>
            <div className="relative flex-1 h-full">
              <div className="absolute top-0 bottom-0 w-px bg-rose-400" style={{ left: `${today}%` }} />
              <div className={`absolute top-2 h-5 rounded ${STATUS_BAR[a.status]} ${a.overdue ? "ring-2 ring-rose-500" : ""} overflow-hidden`} style={{ left: `${l}%`, width: `${w}%` }} title={`${a.start_date} → ${a.due_date} · ${a.percent}%`}>
                <div className="h-full bg-[#153A63]/60" style={{ width: `${a.percent}%` }} />
                <span className="absolute inset-0 px-1 text-[10px] text-white leading-5 truncate">{a.percent}%</span>
              </div>
              {(a.milestones || []).map((m, i) => (new Date(m.date).getTime() >= start.getTime() && new Date(m.date).getTime() < end) && (
                <div key={i} title={`${m.label} · ${m.date}`} className="absolute top-3 h-3 w-3 rotate-45 bg-[#153A63] border border-white" style={{ left: `calc(${pct(m.date)}% - 6px)` }} />
              ))}
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap gap-4 text-[11px] text-slate-500 mt-2 ml-[220px]">
        <span><span className="inline-block w-3 h-3 bg-rose-400 align-middle mr-1" />{tx("Aujourd'hui", "Today")}</span>
        <span><span className="inline-block w-3 h-3 rotate-45 bg-[#153A63] align-middle mr-1" />{tx("Jalon", "Milestone")}</span>
        <span><span className="inline-block w-3 h-3 rounded ring-2 ring-rose-500 align-middle mr-1" />{tx("En retard", "Overdue")}</span>
        {hidden > 0 && <span>{hidden} {tx("action(s) hors fenêtre", "action(s) outside window")}</span>}
      </div>
    </div>
  );
}
